import React, { PureComponent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IStreamData } from "@formant/core/model/IStreamData";
import { ITestableComponentProps } from "@formant/core/model/ITestableComponentProps";
import { Uuid } from "@formant/core/model/Uuid";

import { Lazy } from "../../Lazy";
import { ModuleSize } from "../Module";
import * as styles from "./BitsetModuleRow.scss";
import { IBitsetRowData } from "./IBitsetRowData";

const Graph = Lazy(async () => (await import("../Graph")).Graph);

export interface IBitsetModuleRowProps extends ITestableComponentProps {
    data: IBitsetRowData;
    deviceId: Uuid;
    visible: boolean;
    live?: boolean;
    time: Date;
    startTime: Date;
    endTime: Date;
    size?: ModuleSize;
    onSelectTime?: (time: Date) => void;
    defaultStreamThrottleHz: number;
    columnWidth: number | undefined;
}

@observer
export class BitsetModuleRow extends PureComponent<IBitsetModuleRowProps> {
    private cellRef = React.createRef<HTMLTableCellElement>();
    @observable
    private cellHeight = 40;
    private mutationObserver: MutationObserver;

    constructor(props: IBitsetModuleRowProps) {
        super(props);
        this.mutationObserver = new MutationObserver(() => {
            this.updateGraphHeight();
        });
    }

    public componentDidMount() {
        if (this.cellRef.current) {
            this.mutationObserver.observe(this.cellRef.current, {
                attributes: true,
            });
        }
        this.updateGraphHeight();
    }

    private updateGraphHeight() {
        if (this.cellRef.current) {
            this.cellHeight = this.cellRef.current.clientHeight;
        }
    }

    public componentWillUnmount() {
        this.mutationObserver.disconnect();
    }

    public render() {
        const {
            data,
            deviceId,
            live,
            time,
            startTime,
            endTime,
            visible,
            size,
            onSelectTime,
            defaultStreamThrottleHz,
            columnWidth,
        } = this.props;

        const numeric: IStreamData<"numeric">[] = data.all.flatMap(
            ({ tags, points }) => [
                {
                    deviceId,
                    name: `${data.key} true`,
                    type: "numeric",
                    tags: { ...tags, value: "true" },
                    points: visible
                        ? points
                              .filter(({ value }) => value)
                              .map(({ time: pointTime }) => [
                                  pointTime.getTime(),
                                  1,
                              ])
                        : [],
                },
                {
                    deviceId,
                    name: `${data.key} false`,
                    type: "numeric",
                    tags: { ...tags, value: "false" },
                    points: visible
                        ? points
                              .filter(({ value }) => !value)
                              .map(({ time: pointTime }) => [
                                  pointTime.getTime(),
                                  0,
                              ])
                        : [],
                },
            ],
        );

        return (
            <tr
                className={classnames(
                    styles["bitset-module-row"],
                    size && styles[size],
                )}
                data-testid={`bitset-row-${data.key}`}
                data-value={data.current?.value}
            >
                <td className={styles.status}>
                    <div
                        className={classnames(
                            styles.chip,
                            data.current && data.current.value && styles.on,
                        )}
                    />
                </td>
                <td
                    className={styles.key}
                    ref={this.cellRef}
                    data-tooltip={data.key}
                    style={{
                        width: columnWidth,
                    }}
                >
                    {data.key}
                </td>
                <td className={styles.history}>
                    <div
                        className={styles.history}
                        style={{
                            height: this.cellHeight,
                        }}
                    >
                        {visible && (
                            <Graph
                                defaultStreamThrottleHz={
                                    defaultStreamThrottleHz
                                }
                                data={numeric}
                                time={time}
                                startTime={startTime}
                                endTime={endTime}
                                live={live}
                                min={-1}
                                max={1}
                                mode="bitset"
                                className={styles.graph}
                                onSelectTime={onSelectTime}
                            />
                        )}
                    </div>
                </td>
            </tr>
        );
    }
}
